import { useMutation, useQueryClient } from "@tanstack/react-query";
import invariant from "tiny-invariant";
import { z } from "zod";

import { graphqlClient } from "@/api/graphql";
import { entitySchema } from "@/common/schema/entity";
import { graphql } from "@/generated/digitalnisklady.cz";
import { Currency } from "@/generated/digitalnisklady.cz/graphql";

import { editOfferDocument } from "./edit-offer";

// eslint-disable-next-line lingui/no-unlocalized-strings
const editStorageDocument = graphql(`
  mutation EditStorage($id: ID!, $storage: StorageInput!) {
    editStorage(id: $id, storage: $storage) {
      id
    }
  }
`);

// eslint-disable-next-line lingui/no-unlocalized-strings
const addOfferDocument = graphql(`
  mutation AddOffer($offer: OfferInput!) {
    addOffer(offer: $offer) {
      id
    }
  }
`);

type EditPayload = {
  name: string;
  companyId: string;
  address: string;
  zip: string;
};

const useEditStorage = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, storage }: { id: string; storage: EditPayload }) =>
      await graphqlClient.request(editStorageDocument, {
        id,
        storage: {
          address: storage.address,
          company: storage.companyId,
          currency: Currency.Czk,
          label: storage.name,
          zip: storage.zip,
        },
      }),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({
        predicate: (query) =>
          query.queryKey[0] === "user" ||
          (query.queryKey[0] === "storage" &&
            query.queryKey[1] === result.editStorage?.id),
      });
    },
  });
};

const useAddCrop = () => {
  return useMutation({
    mutationFn: async (payload: {
      storageId: string;
      storage: {
        zip: string;
        label: string;
        crops: {
          id: string;
          tons: number;
        };
        companyId: string;
        address: string;
      };
      harvestYear: number;
    }) => {
      const existingOffers = await cropsAlreadyInStorage({
        storageId: payload.storageId,
      });
      invariant(existingOffers);

      const existingOffer = existingOffers.find(
        (offer) =>
          offer.harvestYear === payload.harvestYear &&
          offer.crop.id === payload.storage.crops.id,
      )?.offer;

      await graphqlClient.request(editStorageDocument, {
        id: payload.storageId,
        storage: {
          address: payload.storage.address,
          company: payload.storage.companyId,
          crops: [
            ...new Set([
              payload.storage.crops.id,
              ...existingOffers.map((offer) => offer.crop.id),
            ]),
          ],
          currency: Currency.Czk,
          label: payload.storage.label,
          zip: payload.storage.zip,
        },
      });

      if (existingOffer) {
        await graphqlClient.request(editOfferDocument, {
          id: existingOffer.id,
          offer: {
            // this is prone to race condition errors
            amount: payload.storage.crops.tons + existingOffer.totalAmount,

            crop: payload.storage.crops.id,

            storage: payload.storageId,
            yearHarvested: payload.harvestYear,
          },
        });
      } else {
        await graphqlClient.request(addOfferDocument, {
          offer: {
            amount: payload.storage.crops.tons,
            crop: payload.storage.crops.id,
            storage: payload.storageId,
            yearHarvested: payload.harvestYear,
          },
        });
      }
    },
  });
};

// eslint-disable-next-line lingui/no-unlocalized-strings
const storageOffersDocument = graphql(`
  query StorageOffers($id: ID!, $archived: Boolean, $locale: String) {
    storage(id: $id) {
      offers(archived: $archived) {
        edges {
          node {
            totalAmount
            id
            crop {
              id
              name(locale: $locale)
            }
            yearHarvested
          }
        }
      }
    }
  }
`);

const cropsAlreadyInStorageSchema = z.array(
  z.object({
    crop: entitySchema,
    harvestYear: z.number(),
    offer: z.object({ id: z.string(), totalAmount: z.number() }),
  }),
);

const cropsAlreadyInStorage = async ({
  storageId,
}: {
  storageId: string;
}): Promise<z.infer<typeof cropsAlreadyInStorageSchema>> => {
  const offers = await graphqlClient.request(storageOffersDocument, {
    archived: false,
    id: storageId,
  });

  const safeOffers = cropsAlreadyInStorageSchema.parse(
    offers.storage?.offers?.edges?.map((edge) => ({
      crop: edge?.node?.crop,
      harvestYear: edge?.node?.yearHarvested,
      offer: {
        id: edge?.node?.id,
        totalAmount: edge?.node?.totalAmount,
      },
    })),
  );

  return safeOffers;
};

export { useAddCrop, useEditStorage };
